// Phase 139.E — Admin widget for the free Posters funnel.
//
// Renders the aggregate counts returned by GET /api/admin/posters-stats:
// total downloads in the window, breakdowns by kit / term / utm source
// / utm campaign / referrer host / day. Everything is read-only; no
// PII is stored on the backend so there's nothing user-level to drill
// into here.
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const StatTable = ({ title, rows, testid, emptyLabel }) => (
  <div
    data-testid={testid}
    className="rounded-lg border border-[#4a6984]/15 bg-white p-4"
  >
    <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/75">
      {title}
    </p>
    {(!rows || rows.length === 0) ? (
      <p className="mt-3 text-sm text-[#4a6984]/60 italic">
        {emptyLabel || "No data yet."}
      </p>
    ) : (
      <ul className="mt-3 space-y-1.5">
        {rows.map((r) => (
          <li
            key={r.key}
            className="flex items-center justify-between gap-3 text-sm font-medium text-[#1c2d3d]"
          >
            <span className="truncate">{r.key}</span>
            <span className="font-extrabold tabular-nums text-[#4a6984]">
              {r.count}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function PostersFunnelPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data: payload } = await axios.get(
          `${API}/admin/posters-stats?days=${days}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setData(payload);
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.response?.data?.detail ||
            "Couldn't load posters funnel stats. Make sure you're signed in as admin.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <div
      className="ae-card p-6 md:p-7 space-y-6"
      data-testid="admin-posters-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
            Posters funnel
          </h3>
          <p className="text-sm text-[#4a6984]/75 mt-1">
            Downloads from the free /posters page. Aggregated, no PII.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="posters-stats-window"
          className="h-9 px-3 rounded-md border border-[#4a6984]/20 bg-white text-sm font-bold text-[#4a6984]"
        >
          {WINDOWS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div
          data-testid="posters-stats-loading"
          className="flex items-center gap-2 text-sm text-[#4a6984]/70"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading funnel data&hellip;
        </div>
      ) : error ? (
        <p data-testid="posters-stats-error" className="text-sm text-red-600 font-medium">
          {error}
        </p>
      ) : (
        <>
          <div
            data-testid="posters-stats-total"
            className="rounded-lg border-2 border-[#96ded1] bg-[#96ded1]/15 p-5"
          >
            <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#1c2d3d]">
              Total downloads · last {data?.window_days} days
            </p>
            <p className="mt-1 font-black text-4xl text-[#1c2d3d] tabular-nums leading-none">
              {data?.total_downloads ?? 0}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatTable
              title="By kit"
              testid="posters-stats-by-kit"
              rows={data?.by_kit || []}
              emptyLabel="No downloads in this window yet."
            />
            <StatTable
              title="By seasonal term"
              testid="posters-stats-by-term"
              rows={data?.by_term || []}
              emptyLabel="No welcome-kit downloads in this window."
            />
            <StatTable
              title="By utm source"
              testid="posters-stats-by-source"
              rows={data?.by_utm_source || []}
            />
            <StatTable
              title="By utm campaign"
              testid="posters-stats-by-campaign"
              rows={data?.by_utm_campaign || []}
            />
            <StatTable
              title="By referrer host"
              testid="posters-stats-by-referrer"
              rows={data?.by_referrer_host || []}
            />
            <StatTable
              title="By day"
              testid="posters-stats-by-day"
              rows={(data?.by_day || []).slice(0, 14)}
              emptyLabel="No downloads to chart yet."
            />
          </div>
        </>
      )}
    </div>
  );
}
